import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Divider, Link, Prose, StackBlock, StackInline, TextAlpha } from '@yonderland/andes-react';
import { basketMutations, checkoutQueryKeys } from '@as-react/common-queries';
import { useAemConfig } from '@as-react/common-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CartTotal } from '../../cart-total/CartTotal';
import { ProductTileOrderOverview } from '../../product-tile/order-overview/ProductTileOrderOverview';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { basketActions } from '../../../redux/basket/actions/BasketActions.creators';
import { useCheckoutStepState, CheckoutStep } from '../../../context/CheckoutStepContext';
import { useOnVoucherChanges } from '../../../hooks/useOnVoucherChanges';
import { makeGetBasketLockState, makeGetBasketMappedProductsState, makeGetBasketPricingState, makeGetBasketTotalQuantityState, makeGetBasketVouchersState, } from '../../../redux/basket/selectors/BasketSelector';
import { axiosClient } from '@as-react/common-utils';
import { useCheckoutConfig } from '../../../context/CheckoutContext';
export const ProductsSummary = () => {
    const { t } = useTranslation([I18N_NAMESPACES.checkout]);
    const { defaultRequestParams, servicesEndpoint, root } = useAemConfig();
    const { step } = useCheckoutStepState();
    const { termsUrl } = useCheckoutConfig();
    const products = useSelector(makeGetBasketMappedProductsState());
    const vouchers = useSelector(makeGetBasketVouchersState());
    const pricing = useSelector(makeGetBasketPricingState());
    const isBasketLocked = useSelector(makeGetBasketLockState());
    const basketTotalQuantity = useSelector(makeGetBasketTotalQuantityState());
    const reduxDispatch = useDispatch();
    const getProducts = () => reduxDispatch(basketActions.createGetAction());
    const { onVoucherChanges } = useOnVoucherChanges();
    const queryClient = useQueryClient();
    const { deleteVoucher } = basketMutations({ defaultRequestParams, servicesEndpoint });
    const { mutate: deleteVoucherMutation } = useMutation(deleteVoucher);
    const handleDeleteVoucher = (barcode) => {
        deleteVoucherMutation(barcode, {
            onSuccess: () => __awaiter(void 0, void 0, void 0, function* () {
                getProducts();
                if (step === CheckoutStep.PAYMENT) {
                    yield queryClient.refetchQueries({ queryKey: checkoutQueryKeys.getPaymentInfo() }); // this will trigger paymentInfo update in CheckoutPaymentContainer
                    yield onVoucherChanges();
                }
                // if (step === CheckoutStep.FAST_CHECKOUT) {
                //   getFastOverviewInfo();
                //   onVoucherChanges();
                // }
            }),
        });
    };
    const { data: terms } = useQuery({
        queryKey: ['terms', termsUrl],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () {
            const axiosConfig = {
                method: 'GET',
                url: termsUrl,
                withCredentials: true,
            };
            const result = yield axiosClient.request(axiosConfig);
            return result.data;
        }),
        enabled: !!termsUrl,
    });
    if (!products) {
        return null;
    }
    return (_jsxs(StackBlock, { gap: "4", children: [_jsxs(StackInline, { gap: "2", itemsAlign: "center", children: [_jsx(TextAlpha, { variant: "titleS", children: t(`${I18N_NAMESPACES.checkout}:checkout.order.title.page`) }), !isBasketLocked && (_jsx(Link, { theme: "neutral", variant: "primary", href: `${root}checkout/basket-overview.html`, testId: "back_to_basket_overview", children: t(`${I18N_NAMESPACES.buy}:buy.edit`) }))] }), _jsx(StackBlock, { gap: "3", children: products.map(product => (_jsxs(Fragment, { children: [_jsx(ProductTileOrderOverview, { product: product }), _jsx(Divider, {})] }, product.productCode))) }), _jsx(CartTotal, Object.assign({ vouchers: vouchers }, pricing, { totalQuantity: basketTotalQuantity, onDeleteVoucher: handleDeleteVoucher })), terms && (_jsx(TextAlpha, { variant: "bodyS", children: _jsx(Prose, { html: terms }) }))] }));
};
